import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { LEGAL_LINKS } from "@/lib/legalTexts";

export function LegalConsentGate() {
  const { user, setUser } = useAuth();
  const [version, setVersion] = useState(null);
  const [changes, setChanges] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) api.get("/legal/version").then(({ data }) => { setVersion(data.version); setChanges(data.changes || []); }).catch(() => {}); }, [user]);
  if (!user || !version || user.role === "admin" || user.legal_accepted === version || (user.created_at && user.created_at >= version)) return null;
  const accept = async () => {
    setBusy(true);
    try { const { data } = await api.post("/legal/accept"); setUser((u) => ({ ...u, legal_accepted: data.version })); toast.success("Danke für deine Zustimmung 💖"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-3xl p-6 space-y-4 [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="legal-consent-dialog">
        <span className="mx-auto w-14 h-14 rounded-2xl pink-gradient text-white flex items-center justify-center shadow-lg shadow-rose-200"><ScrollText className="w-7 h-7" /></span>
        <DialogTitle className="font-display text-xl font-extrabold text-center">Unsere Regeln wurden aktualisiert</DialogTitle>
        <DialogDescription className="text-sm text-slate-600 text-center leading-relaxed">
          Bitte lies dir die Änderungen durch und bestätige, um die Community weiter zu nutzen.
        </DialogDescription>
        {changes.length > 0 && (
          <div className="rounded-2xl bg-rose-50/70 border border-rose-100 p-4 space-y-2" data-testid="legal-consent-changes">
            <p className="text-xs font-extrabold uppercase tracking-[0.15em] text-rose-500">Das ist neu</p>
            <ul className="space-y-1.5">
              {changes.map((c, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-700 leading-snug" data-testid={`legal-consent-change-${i}`}><Check className="w-4 h-4 shrink-0 mt-0.5 text-rose-500" />{c}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-2" data-testid="legal-consent-links">
          {LEGAL_LINKS.filter(([to]) => to !== "/impressum").map(([to, l]) => (
            <Link key={to} to={to} target="_blank" className="px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 text-xs font-bold hover:bg-rose-100" data-testid={`legal-consent-link-${to.slice(1)}`}>{l}</Link>
          ))}
        </div>
        <div>
          <button onClick={accept} disabled={busy} data-testid="legal-consent-accept" className="w-full h-12 rounded-full pink-gradient text-white font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-rose-200 active:scale-[0.98] transition-transform">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Ich stimme zu
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
